import { useEffect, useState } from "react";
import { Loader } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getSelectUnities } from "@/Api/unities";
import { getSelectSectors } from "@/Api/sectors";
import { getSelectPositions } from "@/Api/positions";
import { getSelectWorkSchedules } from "@/Api/workSchedules";
import { activateEmployeeFromUser } from "@/Api/employees";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";

type Option = { id: number | string; name: string };

const OptionSelect = ({
  label,
  placeholder,
  value,
  onChange,
  options,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.id} value={String(option.id)}>
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

type ActivateEmployeeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { uuid: string; name: string } | null;
  onActivated?: (employee: any) => void;
};

export const ActivateEmployeeModal = ({
  open,
  onOpenChange,
  user,
  onActivated,
}: ActivateEmployeeModalProps) => {
  const auth = useAuth();
  const isUnityResponsible =
    auth?.user?.user_type_id == TypeUser.unity_responsible;
  const [unities, setUnities] = useState<{ id: number; name: string }[]>([]);
  const [sectors, setSectors] = useState<Option[]>([]);
  const [positions, setPositions] = useState<Option[]>([]);
  const [workSchedules, setWorkSchedules] = useState<Option[]>([]);
  const [unityId, setUnityId] = useState("");
  const [sectorId, setSectorId] = useState("");
  const [positionId, setPositionId] = useState("");
  const [workScheduleId, setWorkScheduleId] = useState("");
  const [isLoad, setIsLoad] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUnityId(isUnityResponsible ? auth?.user?.unity?.[0]?.id || "" : "");
    setSectorId("");
    setPositionId("");
    setWorkScheduleId("");
    (async () => {
      const unitiesRes = await getSelectUnities();
      setUnities(unitiesRes.data?.response || []);
    })();
  }, [open]);

  // Setor, cargo e horário pertencem a uma unidade: só carrega após escolhê-la.
  useEffect(() => {
    setSectorId("");
    setPositionId("");
    setWorkScheduleId("");
    if (!open || !unityId) {
      setSectors([]);
      setPositions([]);
      setWorkSchedules([]);
      return;
    }
    (async () => {
      const [sectorsRes, positionsRes, schedulesRes] = await Promise.all([
        getSelectSectors(unityId),
        getSelectPositions(unityId),
        getSelectWorkSchedules(unityId),
      ]);
      setSectors(sectorsRes.data?.response || []);
      setPositions(positionsRes.data?.response || []);
      setWorkSchedules(schedulesRes.data?.response || []);
    })();
  }, [open, unityId]);

  const confirm = async () => {
    if (!user || !unityId) return;
    setIsLoad(true);
    const { data, ok } = await activateEmployeeFromUser(user.uuid, {
      unity_id: unityId,
      ...(sectorId && { sector_id: sectorId }),
      ...(positionId && { position_id: positionId }),
      ...(workScheduleId && { work_schedule_id: workScheduleId }),
    });
    setIsLoad(false);
    if (!ok) {
      toast(data?.message || "Erro ao ativar como colaborador!");
      return;
    }
    toast("Colaborador ativado com sucesso!");
    onOpenChange(false);
    onActivated?.(data?.response);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogTitle className="text-2xl">Ativar como colaborador</DialogTitle>
          <DialogDescription className="text-base">
            Deseja transformar {user?.name} em colaborador para bater ponto?
            Informe a unidade e, se quiser, o horário, setor e cargo.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {!isUnityResponsible && (
            <OptionSelect
              label="Unidade"
              placeholder="Selecione a unidade"
              value={unityId}
              onChange={setUnityId}
              options={unities}
            />
          )}
          <OptionSelect
            label="Horário de trabalho"
            placeholder="Selecione o horário"
            value={workScheduleId}
            onChange={setWorkScheduleId}
            options={workSchedules}
          />
          <OptionSelect
            label="Setor"
            placeholder="Selecione o setor"
            value={sectorId}
            onChange={setSectorId}
            options={sectors}
          />
          <OptionSelect
            label="Cargo"
            placeholder="Selecione o cargo"
            value={positionId}
            onChange={setPositionId}
            options={positions}
          />
        </div>
        <DialogFooter className="mt-3 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Agora não
          </Button>
          <Button
            className={cn("flex gap-2", colors.bg_primary)}
            disabled={isLoad || !unityId}
            onClick={confirm}
          >
            {isLoad ? (
              <>
                <Loader className="animate-spin size-4" /> Ativando
              </>
            ) : (
              "Ativar colaborador"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
